import React from 'react';
import './ApplicationConfirmation.css';

export interface CoordinatorContact {
  name?: string;
  email: string;
  phone?: string;
}

interface ApplicationConfirmationProps {
  applicationId?: string;
  eventTitle?: string;
  applicantName?: string;
  applicantEmail?: string;
  submissionDate?: string;
  isNewAccount?: boolean;
  /** Event coordinator contact for "Questions?" section. Email required; phone shown only if provided. */
  coordinatorContact?: CoordinatorContact | null;
  /** Show link to complete full attendee profile after minimal registration. */
  showFinishProfile?: boolean;
}

const ApplicationConfirmation: React.FC<ApplicationConfirmationProps> = ({
  applicationId,
  eventTitle = "OBP Italy 2026",
  applicantName,
  applicantEmail,
  submissionDate,
  isNewAccount = false,
  coordinatorContact = null,
  showFinishProfile = false,
}) => {
  return (
    <div className="application-confirmation">
      <div className="confirmation-container">
        <div className="success-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" fill="#10B981" stroke="#10B981" strokeWidth="2"/>
            <path d="M8 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <h1 className="confirmation-title">Application Submitted Successfully!</h1>
        
        <div className="confirmation-details">
          <h2>Thank you for your application</h2>
          <p>
            Your application for <strong>{eventTitle}</strong> has been submitted successfully.
            {applicantName && <span> We've received your application, {applicantName}.</span>}
          </p>
          
          {applicationId && (
            <div className="application-id">
              <strong>Application ID:</strong> {applicationId}
            </div>
          )}
          
          {submissionDate && (
            <div className="submission-date">
              <strong>Submitted on:</strong> {new Date(submissionDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>
        
        {isNewAccount && applicantEmail && (
          <div className="account-setup" style={{
            background: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ color: '#1E40AF', marginBottom: '0.5rem' }}>Complete Your Account Setup</h3>
            <p style={{ marginBottom: '1rem' }}>
              Set up your account to easily sign in and track your application status.
              You can create a password or connect with Google, Facebook, or Apple.
            </p>
            <a
              href="/account-setup"
              className="btn btn-primary"
              style={{ display: 'inline-block' }}
            >
              <i className="fas fa-user-cog me-2" aria-hidden></i>
              Set Up My Account
            </a>
          </div>
        )}

        {showFinishProfile && (
          <div
            className="account-setup"
            style={{
              background: '#F0FDF4',
              border: '1px solid #BBF7D0',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <h3 style={{ color: '#166534', marginBottom: '0.5rem' }}>Finish Your Profile</h3>
            <p style={{ marginBottom: '1rem' }}>
              You can add emergency contact, faith information, and other details whenever you are ready.
            </p>
            <a href="/profile/complete" className="btn btn-success" style={{ display: 'inline-block' }}>
              <i className="fas fa-user-edit me-2" aria-hidden></i>
              Finish My Profile
            </a>
          </div>
        )}

        <div className="next-steps">
          <h3>What happens next?</h3>
          <p>
            You will be notified when your application is accepted and someone will reach out to you with additional details.
          </p>
        </div>
        
        <div className="confirmation-actions">
          <a href="/dashboard" className="btn btn-primary">
            <i className="fas fa-tachometer-alt me-2" aria-hidden></i>
            View Dashboard
          </a>
          <a href="/events" className="btn btn-outline">
            <i className="fas fa-calendar-alt me-2" aria-hidden></i>
            View Other Events
          </a>
        </div>
        
        {coordinatorContact?.email && (
          <div className="contact-info">
            <h4>Questions?</h4>
            <p>
              If you have any questions about your application or the mission trip,
              please contact{coordinatorContact.name ? ` ${coordinatorContact.name}` : ' us'} at{' '}
              <a href={`mailto:${coordinatorContact.email}`}>{coordinatorContact.email}</a>
              {coordinatorContact.phone?.trim() ? (
                <> or call <a href={`tel:${coordinatorContact.phone.trim()}`}>{coordinatorContact.phone.trim()}</a></>
              ) : null}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationConfirmation;
